import React, { useCallback, useState } from 'react';
import { Col, Row } from 'reactstrap';

import dayjs, { dateInputFieldFormat } from '~libs/dayjs';
import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ContainerBanner, ErrorResponse, Select, Spinner, SummaryStatCard } from '~components';
import { faExclamationTriangle, faInfoCircle } from '~components/icon';

import { useLazyGetAcfrReportQuery } from '~support-at-home/actions/sahReports';

// The QFR reports the financial year to date, so every quarter runs from the start of the financial year.
const FY_DATE_FROM = '2026-07-01';
const FY_LABEL = '2026-27';

interface QfrQuarter {
  value: number;
  label: string;
  dateTo: string;
  period: string;
  due: string;
}

export const QFR_QUARTERS: QfrQuarter[] = [
  { value: 1, label: 'Q1', dateTo: '2026-09-30', period: '1 Jul 2026 - 30 Sep 2026', due: '4 Nov 2026' },
  { value: 2, label: 'Q2', dateTo: '2026-12-31', period: '1 Jul 2026 - 31 Dec 2026', due: '14 Feb 2027' },
  { value: 3, label: 'Q3', dateTo: '2027-03-31', period: '1 Jul 2026 - 31 Mar 2027', due: '5 May 2027' },
  { value: 4, label: 'Q4', dateTo: '2027-06-30', period: '1 Jul 2026 - 30 Jun 2027', due: '4 Aug 2027' },
];

const QUARTER_OPTIONS = QFR_QUARTERS.map(({ value, label, period }) => ({ value, label: `${label} (${period})` }));

// The latest quarter that has already ended, or Q1 before any has.
export const defaultQuarter = (today: string = dayjs().format(dateInputFieldFormat)): number => {
  const ended = QFR_QUARTERS.filter(({ dateTo }) => dateTo < today);
  return ended.length ? ended[ended.length - 1].value : QFR_QUARTERS[0].value;
};

const findQuarter = (value: number): QfrQuarter => QFR_QUARTERS.find((q) => q.value === value) ?? QFR_QUARTERS[0];

const QFRTab: React.FC<{ isActive?: boolean }> = () => {
  const [quarter, setQuarter] = useState<number>(() => defaultQuarter());

  // Use currentData, not data: data keeps the previous run's result after a later run fails, which
  // would put an old figure under a new quarter. The quarter shown is read from the request itself,
  // so the figure and its label always come from the same run.
  const [fetchReport, { currentData, originalArgs, isFetching, isError, error, isUninitialized }] =
    useLazyGetAcfrReportQuery();

  const selected = findQuarter(quarter);
  const shown = QFR_QUARTERS.find(({ dateTo }) => dateTo === originalArgs?.date_to) ?? null;

  const handleRunReport = useCallback(() => {
    if (isFetching) return;
    fetchReport({ date_from: FY_DATE_FROM, date_to: selected.dateTo });
  }, [fetchReport, isFetching, selected]);

  return (
    <div className="p-2">
      <ContainerBanner
        className="mb-3 border rounded"
        color="light"
        icon={faInfoCircle}
        dataCy="qfr-notice-bar"
        messages={[
          `Support at Home Operating Income for the Quarterly Financial Report, financial year ${FY_LABEL} to date · whole dollars · Same income as the ACFR Report's Total Income for the same dates · The other QFR figures come from your own accounts and payroll`,
        ]}
      />

      <div className="d-flex align-items-end gap-3 mb-3 flex-wrap" data-cy="qfr-report-controls">
        <div data-cy="qfr-quarter-select">
          <Select
            id="qfr-quarter"
            name="qfr-quarter"
            label="Quarter"
            ariaLabel="Quarter"
            options={QUARTER_OPTIONS}
            value={quarter}
            onChange={(_, value) => setQuarter(Number(value))}
          />
        </div>
        <div className="text-muted small pb-2" data-cy="qfr-due-date">
          {`QFR due ${selected.due}`}
        </div>
        <Button
          color="success"
          size="sm"
          className="mb-1"
          onClick={handleRunReport}
          disabled={isFetching}
          dataCy="qfr-report-run-button"
          aria-label="Run Report"
        >
          {isFetching ? <Spinner loading type="beat" size={8} /> : 'Run Report'}
        </Button>
      </div>

      {isError && <ErrorResponse message={getErrorMessage(error)} />}

      {currentData && !isFetching && currentData.unmapped_count > 0 && (
        <ContainerBanner
          className="mb-3 border rounded"
          color="warning"
          icon={faExclamationTriangle}
          dataCy="qfr-unmapped-warning"
          messages={[
            `${currentData.unmapped_count} claim items could not be categorised. Check that all items have a budget and service type assigned before submitting your QFR.`,
          ]}
        />
      )}

      {isFetching && (
        <div className="text-center py-5" data-cy="qfr-report-loading">
          <Spinner loading type="beat" size={12} />
        </div>
      )}

      {!isFetching && currentData && shown && (
        <Row className="mb-4 g-3">
          <Col md={6} data-cy="qfr-income-card">
            <SummaryStatCard
              title={`${shown.label} ${FY_LABEL} · ${shown.period} · due ${shown.due}`}
              count={formatCurrency(currentData.totals.total_income, 0)}
              countLabel="Support at Home Operating Income (year to date)"
            />
          </Col>
          <Col xs={12}>
            <p className="text-muted small mb-0" data-cy="qfr-acfr-total">
              {`ACFR Report Total Income for ${shown.period}: ${formatCurrency(currentData.totals.total_income)}`}
            </p>
          </Col>
        </Row>
      )}

      {isUninitialized && !isFetching && (
        <div className="text-center text-muted py-5" data-cy="qfr-report-uninitialised">
          Select a quarter and click <strong>Run Report</strong> to get the year-to-date Support at Home income.
        </div>
      )}
    </div>
  );
};

export default QFRTab;
